// 🔧 content_defaults.js
// ✅ Purpose: Creates realistic Event Content settings for Seed Engine events
// ✅ Features:
// - Builds the same fields managed by settings_content.js
// - Writes content used directly by event_home.js
// - Supports industry-specific messaging
// - Future-ready for AI-generated content
// - Fully block-commented for clarity and future devs

import { getPowerUpProfile } from "../industries/power_up_profiles";

export const buildContentSettings = ({
  eventName,
  industryTemplate,
  eventEnd,
} = {}) => {
  const industryLabel = industryTemplate?.label || "Car Show";
  const entryLabel = industryTemplate?.entryLabel || "entry";
  const categoryLabel = industryTemplate?.categoryLabel || "category";
  const content = industryTemplate?.content || {};
  const vendorCategories = Array.from(
    new Set(
      getPowerUpProfile(industryTemplate?.key).vendors.map(([, type]) => type)
    )
  ).map((name) => ({
    name,
    paymentRequired: false,
    amountCents: 0,
  }));
  const eventEndDate = eventEnd?.toDate
    ? eventEnd.toDate()
    : eventEnd instanceof Date
    ? eventEnd
    : null;
  const sponsorAwardResultsShowAt = eventEndDate
    ? [
        eventEndDate.getFullYear(),
        String(eventEndDate.getMonth() + 1).padStart(2, "0"),
        String(eventEndDate.getDate()).padStart(2, "0"),
      ].join("-") +
      `T${String(eventEndDate.getHours()).padStart(2, "0")}:${String(
        eventEndDate.getMinutes()
      ).padStart(2, "0")}`
    : "";

  return {
    description: content.description || `
      <p><strong>Welcome to the ${eventName}.</strong></p>
      <p>
        Explore a fully prepared ${industryLabel.toLowerCase()} experience with realistic
        ${entryLabel.toLowerCase()} details, ${categoryLabel.toLowerCase()} groupings, schedules,
        maps, sponsors, volunteers, voting, and results.
      </p>
      <p>
        This demo is designed to show organizers and attendees how Let's Go Vote brings the
        complete event journey together in one mobile-friendly experience.
      </p>
    `,

    participationTips: content.participationTips || `
      <p><strong>Participation and Voting Tips</strong></p>
      <ul>
        <li>Browse every ${entryLabel.toLowerCase()} and review its details before choosing.</li>
        <li>Scan an on-site QR code or open an entry from the public event page.</li>
        <li>Submit a vote in each available ${categoryLabel.toLowerCase()}.</li>
        <li>Open My Ballot to review or update your selections while voting is open.</li>
        <li>Check the schedule, map, sponsor information, and volunteer opportunities during your visit.</li>
      </ul>
    `,

    optionalDetails1: content.optionalDetails1 || `
      <p><strong>Plan Your Visit</strong></p>
      <p>
        Arrive early enough to explore the full event, meet participants, visit supporting
        sponsors, and review the available choices before voting. Use the event map and schedule
        to make the most of your day.
      </p>
    `,

    optionalDetails2: content.optionalDetails2 || `
      <p><strong>How Results Work</strong></p>
      <p>
        Let's Go Vote securely organizes submitted ballots by ${categoryLabel.toLowerCase()} and
        makes results available according to the organizer's publishing schedule. Demo data lets
        you explore the complete workflow without affecting a live event.
      </p>
    `,

    feedbackNote: content.feedbackNote || `
      <p>
        Tell the event team what you enjoyed and what would make the next ${industryLabel.toLowerCase()}
        even better. Attendee feedback helps improve future experiences.
      </p>
    `,

    contactNote: content.contactNote || `
      <p><strong>Questions before your shift?</strong></p>
      <p>Contact the event promoter through the event contact information. On event day, check in at the volunteer welcome area before your scheduled start time.</p>
    `,

    volunteerGoodies: content.volunteerGoodies || `
      <p>Demo volunteers receive an event credential, refreshments during their shift, and a volunteer appreciation item while supplies last.</p>
    `,

    volunteerThanks: content.volunteerThanks || `
      <p><strong>Thank you for volunteering!</strong></p>
      <p>Your time helps participants, attendees, and organizers enjoy a welcoming and well-run event.</p>
    `,

    sponsorsNote: content.sponsorsNote || `
      <p><strong>Support ${eventName} as an event sponsor.</strong></p>
      <p>Sponsorship connects your organization with attendees while helping fund event operations, participant experiences, awards, and community programming. Contact the event promoter to discuss available recognition levels and benefits.</p>
    `,

    vendorsNote: content.vendorsNote || `
      <p><strong>Explore the vendors supporting ${eventName}.</strong></p>
      <p>Visit the vendor directory to discover food, merchandise, services, and community partners. Use booth numbers and map locations to plan your stops during the event.</p>
    `,

    vendorCategories,

    sponsorAwardsEnabled: true,
    sponsorAwardResultsShowAt,
  };
};
